/**
 * `ForbiddenScreen` — GVW-162 standalone state page.
 *
 * Access denied / 403 / RLS page-level error state. Reads ?reason= and 
 * ?correlation= query params. Live region role='alert' per error-catalog.md.
 *
 * Cite: design/30-engineering/ui-implementation/GVW-162/brief.md
 * Copy: error-catalog.md GV-ERR-403, copy-deck.md gvw.162.*
 */

import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { tokens } from '@/design';
import { useLiveRegion } from '@/state/liveRegion';

export function ForbiddenScreen() {
  const liveRegion = useLiveRegion();
  const [searchParams] = useSearchParams();
  
  // Query params per brief.md step 4.f
  const correlationId = searchParams.get('correlation') || 'N/A';
  const orgName = searchParams.get('org') || 'this organization';

  useEffect(() => {
    // Copy key: gvw.162.title
    document.title = 'Access denied · GeoViewer';
    // Alert role per error-catalog.md GV-ERR-403
    liveRegion.announce('alert', 'Access denied');
  }, [liveRegion]);

  return (
    <main
      role="main"
      aria-labelledby="forbidden-heading"
      data-gvw-id="GVW-162"
      data-route="/403"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: tokens.color.surface[0],
        color: tokens.color.ink.primary,
        fontFamily: tokens.font.family.ui,
        padding: tokens.space[5],
        boxSizing: 'border-box',
      }}
    >
      <section
        style={{
          maxWidth: tokens.layout.standaloneStateExtraWide,
          width: '100%',
          background: tokens.color.surface[1],
          border: tokens.border.thin,
          borderLeft: `${tokens.space[1]}px solid ${tokens.color.semantic.danger}`,
          borderRadius: tokens.radius.lg,
          padding: `${tokens.space[6]}px ${tokens.space[5]}px`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: tokens.space[4],
          textAlign: 'center',
        }}
      >
        {/* Hero glyph per brief.md icon plan */}
        <div
          aria-hidden="true"
          style={{
            fontSize: 32,
            color: tokens.color.semantic.danger,
            fontFamily: 'Material Symbols Outlined',
          }}
        >
          block
        </div>

        {/* Copy key: gvw.162.title from copy-deck.md */}
        <h1
          id="forbidden-heading"
          style={{
            margin: 0,
            fontSize: tokens.font.size['2xl'],
            lineHeight: tokens.font.lineHeight.tight,
            fontWeight: tokens.font.weight.semibold,
            color: tokens.color.ink.primary,
          }}
        >
          Access denied
        </h1>

        {/* Copy key: gvw.162.body with orgName substitution */}
        <p
          style={{
            margin: 0,
            fontSize: tokens.font.size.md,
            lineHeight: tokens.font.lineHeight.normal,
            color: tokens.color.ink.secondary,
            maxWidth: '50ch',
          }}
        >
          You do not have access to this page in {orgName}. Switch to an organization that has access, or request access.
        </p>

        {/* Recovery actions per navigation-map.md §8 */}
        <div
          style={{
            display: 'flex',
            gap: tokens.space[3],
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
        >
          <button
            type="button"
            onClick={() => {
              // Switch org per brief.md §10
              window.location.href = '/workspace';
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: tokens.space[2],
              padding: `${tokens.space[3]}px ${tokens.space[4]}px`,
              background: tokens.color.accent.primary,
              color: tokens.color.accent.on,
              border: 'none',
              borderRadius: tokens.radius.md,
              fontSize: tokens.font.size.md,
              fontWeight: tokens.font.weight.medium,
              fontFamily: 'inherit',
              cursor: 'pointer',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                fontFamily: 'Material Symbols Outlined',
                fontSize: 18,
              }}
            >
              business
            </span>
            Switch organization
          </button>

          <button
            type="button"
            onClick={() => {
              window.open('/admin/request-access', '_blank');
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: tokens.space[2],
              padding: `${tokens.space[3]}px ${tokens.space[4]}px`,
              background: tokens.color.surface[2],
              color: tokens.color.ink.primary,
              border: tokens.border.thin,
              borderRadius: tokens.radius.md,
              fontSize: tokens.font.size.md,
              fontWeight: tokens.font.weight.medium,
              fontFamily: 'inherit',
              cursor: 'pointer',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                fontFamily: 'Material Symbols Outlined',
                fontSize: 18,
              }}
            >
              support_agent
            </span>
            Request access
          </button>
        </div>

        {/* Correlation ID per brief.md §8 + accessibility plan */}
        {correlationId && (
          <div
            style={{
              marginTop: tokens.space[2],
              fontSize: tokens.font.size.sm,
              color: tokens.color.ink.muted,
            }}
          >
            <span>Reference: </span>
            <code
              style={{
                fontFamily: tokens.font.family.mono,
                background: tokens.color.surface[2],
                padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
                borderRadius: tokens.radius.sm,
                userSelect: 'all',
              }}
            >
              {correlationId}
            </code>
          </div>
        )}
      </section>
    </main>
  );
}